import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUserAttributes } from "aws-amplify/auth";
import { signOut } from "aws-amplify/auth";
import { invalidateAllUserLessonCaches } from "../../utils/userLessonUtils";
import { useAdminCheck } from "../../hooks/useAdminCheck";
import "./AppBar.css"; // Import the CSS file
import {
  Box,
  Button,
  IconButton,
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { AccountCircle, LocalHospitalTwoTone } from "@mui/icons-material";

function AppBar() {
  const [userAttributes, setUserAttributes] = useState(null);
  const [userFirstName, setUserFirstName] = useState(null);
  const [userLastName, setUserLastName] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");

  const navigate = useNavigate();
  
  // Check if user is admin
  const { isAdmin } = useAdminCheck();

  // Handle Menu Click
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Fetch User Attributes
  useEffect(() => {
    async function fetchUserData() {
      try {
        setUserAttributes(await fetchUserAttributes());
      } catch (error) {
        setAlertMessage("Error fetching user attributes.");
        setAlertSeverity("error");
      }
    }

    fetchUserData();
  }, []);

  // Set values for user's data after fetching
  useEffect(() => {
    if (userAttributes) {
      setUserFirstName(userAttributes.given_name);
      setUserLastName(userAttributes.family_name);
    }
  }, [userAttributes, userFirstName, userLastName]);

  // Signout function
  async function handleSignOut() {
    handleClose();
    try {
      await signOut();
      // Clear all user lesson caches on sign out to ensure clean state
      invalidateAllUserLessonCaches();
      console.log('Cleared all user lesson caches on sign out');
      // Navigate to root route after successful logout
      navigate('/');
    } catch (error) {
      setAlertMessage("Error signing out.");
      setAlertSeverity("error");
    }
  }

  // Function to handle settings click
  function handleSettingsClick() {
    handleClose();
    
    // Double-check admin status before navigation
    if (isAdmin) {
      navigate("/settings");
    } else {
      setAlertMessage("Access denied. Admin privileges required.");
      setAlertSeverity("error");
    }
  }

  const renderAlert = (severity, message, onClose) => (
    <Alert severity={severity} onClose={onClose} sx={{ marginBottom: 2, width: '100%' }}>
      <AlertTitle>{severity === 'success' ? 'Success' : 'Error'}</AlertTitle>
      {message}
    </Alert>
  );



  function renderTitle() {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 2 }}>
        <Button
          startIcon={<LocalHospitalTwoTone />}
          onClick={() => navigate('/')}
          sx={{ color: 'inherit', textTransform: 'none' }}
        >
          <Typography variant="h6" component="div">
            Drepanocitico
          </Typography>
        </Button>
        <Button
          onClick={() => navigate('/lecciones')}
          sx={{ color: 'inherit', textTransform: 'none' }}
        >
          <Typography variant="h6" component="div">
            Lecciones
          </Typography>
        </Button>
      </Box>
    );
  }

  function renderManageMenu() {
    return (
      <div>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleMenu}
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {/* Only show Settings if user is admin */}
          {isAdmin && (
            <MenuItem onClick={handleSettingsClick}>Configuración</MenuItem>
          )}
          <MenuItem onClick={handleSignOut}>Cerrar sesión</MenuItem>
        </Menu>
      </div>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {alertMessage && renderAlert(alertSeverity, alertMessage, () => setAlertMessage(""))}
      <MuiAppBar position="static">
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {renderTitle()}
          {renderManageMenu()}
        </Toolbar>
      </MuiAppBar>
    </Box>
  );
}

export default AppBar;